// <donut-chart> — the revenue-breakdown donut, with the "62% / digital" or
// "$51K / top 5" centre label drawn by a per-chart plugin. Same contract as
// <stacked-bar-chart>: JSON payload script + sized canvas wrapper, element
// only draws. The value/label legend beside it is server-rendered HTML.
//
//   <donut-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative size-40"><canvas></canvas></div>
//   </donut-chart>

import { Chart, type Plugin } from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	money,
	readPayload,
} from "./chart-theme";

type Payload = {
	currency: string;
	center: { value: string; label: string };
	slices: { label: string; value: number; color: string }[];
};

// Draws the two-line centre text inside the cutout. Built per chart so the
// closure carries this donut's text — plugins registered globally would
// stamp every doughnut with the same label.
function centerText(center: Payload["center"]): Plugin<"doughnut"> {
	return {
		id: "donutCenterText",
		afterDatasetsDraw(chart) {
			const { ctx, chartArea } = chart;
			const x = (chartArea.left + chartArea.right) / 2;
			const y = (chartArea.top + chartArea.bottom) / 2;
			const family = Chart.defaults.font.family ?? "ui-monospace, monospace";

			ctx.save();
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillStyle = "#ffffff";
			ctx.font = `500 17px ${family}`;
			ctx.fillText(center.value, x, y - 8);
			ctx.fillStyle = "rgba(255, 255, 255, 0.45)";
			ctx.font = `400 11px ${family}`;
			ctx.fillText(center.label, x, y + 11);
			ctx.restore();
		},
	};
}

class DonutChart extends HTMLElement {
	#chart: Chart<"doughnut"> | null = null;

	connectedCallback() {
		ensureChartTheme();
		const payload = readPayload<Payload>(this, "donut-chart");

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "donut-chart"), {
			type: "doughnut",
			data: {
				labels: payload.slices.map((slice) => slice.label),
				datasets: [
					{
						data: payload.slices.map((slice) => slice.value),
						backgroundColor: payload.slices.map((slice) =>
							chartColor(slice.color),
						),
						// Hairline gaps between slices, cut in the card surface colour.
						borderColor: chartColor("--color-panel"),
						borderWidth: 2,
						hoverOffset: 5,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: "72%",
				animation: { duration: 300 },
				// Headroom for hoverOffset so lifted slices don't clip.
				layout: { padding: 5 },
				plugins: {
					tooltip: {
						callbacks: {
							label: (item) =>
								` ${item.label}: ${money(item.parsed, payload.currency)}`,
						},
					},
				},
			},
			plugins: [centerText(payload.center)],
		});
	}

	disconnectedCallback() {
		this.#chart?.destroy();
		this.#chart = null;
	}
}

if (!customElements.get("donut-chart")) {
	customElements.define("donut-chart", DonutChart);
}
